import React from "react";
import {
  addField,
  getDefaultSchemaForFieldType,
  makeUniqueFieldName,
  type PathArray,
  type SubmissionFieldTypeWithSchema,
} from "common/utils/formStructure";
import {
  buildPMVAdjustmentRatioFormula,
  buildPMVAutomatedValueFormula,
  buildPMVImprovementValueFormula,
  buildPMVSquareFootageValueFormula,
} from "common/utils/submissions";
import { FlexColumn } from "../../../../../../Common/__styles__/Layout";
import { Body } from "../../../../../../Common/Typography";
import { SelectionCard } from "../../../../../../Inputs/SelectionCard";
import { fieldCategoryToFieldTypesMap } from "../../constants";
import { EDITOR_TYPE, editorTypeToFieldPermissionsMap } from "../../editorType";
import { useBuilderFormContext, useBuilderWatch } from "../../form/form";
import { VIEWER_TYPE, viewerTypeToFieldPermissionsMap } from "../../viewerType";

type FieldsListProps = {
  atFieldPath: PathArray;
};

export function FieldsList({ atFieldPath }: FieldsListProps) {
  const { getValues, setValue } = useBuilderFormContext();
  const getDefaultSchema = useGetDefaultSchemaForFieldType();

  const onChange = (fieldType: SubmissionFieldTypeWithSchema) => {
    const fieldName = makeUniqueFieldName();
    let fieldSchema = getDefaultSchema(fieldType);

    if (fieldType === "PropertyMarketValue") {
      const fieldPath = [...atFieldPath, fieldName].join(".");
      fieldSchema = {
        ...fieldSchema,
        schema: {
          ...fieldSchema.schema,
          oneOf: fieldSchema.schema.oneOf?.map((option: any) => {
            if (option.name === "automatedAssessedValue") {
              return {
                ...option,
                properties: {
                  ...option.properties,
                  improvementValue: {
                    ...option.properties.improvementValue,
                    formula: buildPMVImprovementValueFormula(fieldPath),
                  },
                  adjustmentRatio: {
                    ...option.properties.adjustmentRatio,
                    formula: buildPMVAdjustmentRatioFormula(fieldPath),
                  },
                  value: {
                    ...option.properties.value,
                    formula: buildPMVAutomatedValueFormula(fieldPath),
                  },
                },
              };
            }
            if (option.name === "squareFootageEstimate") {
              return {
                ...option,
                properties: {
                  ...option.properties,
                  value: {
                    ...option.properties.value,
                    formula: buildPMVSquareFootageValueFormula(fieldPath),
                  },
                },
              };
            }
            return option;
          }),
        },
      };
    }

    setValue(
      "formStructure",
      addField({
        formStructure: getValues().formStructure,
        fieldSchema,
        fieldName,
        targetPath: atFieldPath,
      }),
      { shouldDirty: true },
    );
  };

  return (
    <FlexColumn gap="l">
      {Object.entries(fieldCategoryToFieldTypesMap).map(
        ([category, fieldTypes]) => (
          <FlexColumn key={category} gap="s">
            <Body size="default" type="emphasis">
              {category}
            </Body>
            <SelectionCard.Group
              direction="vertical"
              spacing="compact"
              handleSelection={onChange}
            >
              {fieldTypes.map((fieldType, index) => (
                <SelectionCard.Item
                  key={index}
                  value={fieldType.name}
                  title={fieldType.label}
                  aria-label={`Add ${fieldType.label} field`}
                  borderStyle="ghost"
                  size="compact"
                >
                  <SelectionCard.CommonContent
                    title={fieldType.label}
                    iconName={fieldType.icon}
                  />
                </SelectionCard.Item>
              ))}
            </SelectionCard.Group>
          </FlexColumn>
        ),
      )}
    </FlexColumn>
  );
}

type PermissionsOptions = {
  canCreateRolesAndGroupIds: Array<string>;
  hiddenFromPublicByDefault: boolean;
};

function getDefaultFieldPermissions({
  canCreateRolesAndGroupIds,
  hiddenFromPublicByDefault,
}: PermissionsOptions) {
  return {
    "ui:viewOnCreate": canCreateRolesAndGroupIds,
    "ui:viewOnRetrieve":
      hiddenFromPublicByDefault ?
        viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM]
      : viewerTypeToFieldPermissionsMap[VIEWER_TYPE.TEAM_AND_GUESTS],
    "ui:canUpdate": editorTypeToFieldPermissionsMap[EDITOR_TYPE.TEAM],
  };
}

export function getDefaultSchemaForFieldTypeWithPermissions<
  FieldType extends SubmissionFieldTypeWithSchema,
>({
  fieldType,
  canCreateRolesAndGroupIds,
  hiddenFromPublicByDefault,
}: { fieldType: FieldType } & PermissionsOptions) {
  const defaultSchema = getDefaultSchemaForFieldType(fieldType);

  if (fieldType === "group") {
    return defaultSchema;
  }

  return {
    ...defaultSchema,
    uiSchema: {
      ...defaultSchema.uiSchema,
      ...getDefaultFieldPermissions({
        canCreateRolesAndGroupIds,
        hiddenFromPublicByDefault,
      }),
    },
  };
}

function useGetDefaultSchemaForFieldType() {
  const [canCreateRolesAndGroupIds, hiddenFromPublicByDefault] =
    useBuilderWatch(["canCreateRolesAndGroupIds", "hiddenFromPublicByDefault"]);

  return (fieldType: SubmissionFieldTypeWithSchema) => {
    return getDefaultSchemaForFieldTypeWithPermissions({
      fieldType,
      canCreateRolesAndGroupIds,
      hiddenFromPublicByDefault,
    });
  };
}
